import { lock, unlock } from './scroll-lock.js';

const FAIL = "Couldn't update your bag — try again";
const controllers = new WeakMap();

function makeMoney(moneyFormat) {
  const group = (n, sep) => n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return (cents) => {
    const d = (cents / 100).toFixed(2);
    const [whole, frac] = d.split('.');
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
      if (k === 'amount') return group(whole, ',') + '.' + frac;
      if (k === 'amount_no_decimals') return group(whole, ',');
      if (k === 'amount_with_comma_separator') return group(whole, '.') + ',' + frac;
      if (k === 'amount_no_decimals_with_comma_separator') return group(whole, '.');
      return d;
    });
  };
}

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

async function getJSON(url, opts) {
  const res = await fetch(url, Object.assign({ headers: { Accept: 'application/json' } }, opts));
  if (!res.ok) throw new Error(url + ' ' + res.status);
  return res.json();
}

export function init(dialog) {
  if (dialog.dataset.cartInit) return;
  dialog.dataset.cartInit = '1';
  const ac = new AbortController();
  const { signal } = ac;
  controllers.set(dialog, ac);
  window.__hssDrawer = true; // adders skip their toast when the drawer will show the add

  const money = makeMoney(dialog.dataset.moneyFormat || '${{amount}}');
  const threshold = Number(dialog.dataset.freeThreshold) || 0;
  const $ = (s) => dialog.querySelector(s);
  const linesEl = $('[data-lines]');
  const emptyEl = $('[data-empty]');
  const shipEl = $('[data-ship]');
  const subEl = $('[data-subtotal]');
  const liveEl = $('[data-live]');
  const closeBtn = $('.cart__x');
  const headEl = $('.cart__head');
  const bodyEl = $('[data-body]');
  const footEl = $('[data-foot]');
  const picker = $('[data-picker]');
  const pickerOpts = $('[data-picker-opts]');
  const pickerTitle = $('[data-picker-title]');
  const pickerBack = $('[data-picker-close]');

  let opener = null;
  let locked = false;
  let busy = false;
  let pickerSeq = 0;

  const toggleBtn = () => document.querySelector('[data-cart-toggle]');
  const announce = (msg) => {
    liveEl.textContent = '';
    // Re-set on the next frame so repeated identical messages are still announced.
    requestAnimationFrame(() => { liveEl.textContent = msg; });
  };

  /* ---------- open / close ---------- */
  function open(from) {
    if (dialog.open) return;
    opener = from || (document.activeElement !== document.body ? document.activeElement : null) || toggleBtn();
    dialog.showModal();
    if (!locked) { locked = true; lock(); }
    closeBtn.focus();
  }
  function close() {
    if (dialog.open) dialog.close();
  }
  dialog.addEventListener('close', () => {
    if (locked) { locked = false; unlock(); }
    hidePicker();
    const back = opener && opener.isConnected && !opener.disabled ? opener : toggleBtn();
    opener = null;
    if (back) back.focus();
  }, { signal });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) { close(); return; } // ::backdrop click targets the dialog itself
    if (e.target.closest('[data-cart-close]')) close();
  }, { signal });

  // showModal() traps focus natively, but Tab from the last control would leave the page for the
  // browser UI; wrap explicitly so focus never leaves the drawer.
  const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  dialog.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const items = [...dialog.querySelectorAll(FOCUSABLE)].filter((n) =>
      !n.disabled && n.tabIndex >= 0 && !n.closest('[hidden],[inert]') && n.getClientRects().length > 0);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialog.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }, { signal });

  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-cart-toggle]');
    if (!t) return;
    e.preventDefault();
    open(t);
  }, { signal });

  /* ---------- render ---------- */
  function snapshot() {
    const map = {};
    dialog.querySelectorAll('.line').forEach((li) => {
      map[li.dataset.key] = { qty: Number(li.dataset.qty), title: li.dataset.title };
    });
    return map;
  }

  function lineNode(item) {
    const li = el('li', 'line');
    li.dataset.key = item.key;
    li.dataset.qty = item.quantity;
    li.dataset.title = item.product_title;

    const thumb = el('a', 'line__thumb');
    thumb.href = item.url;
    thumb.tabIndex = -1;
    thumb.setAttribute('aria-hidden', 'true');
    const box = el('div', 'img img--4x5 img--contain');
    const src = item.featured_image && item.featured_image.url;
    if (src) {
      const img = document.createElement('img');
      img.src = src + (src.includes('?') ? '&' : '?') + 'width=160';
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      box.append(img);
    } else {
      box.classList.add('img--placeholder');
    }
    thumb.append(box);

    const info = el('div', 'line__info');
    const title = el('a', 'line__title', item.product_title);
    title.href = item.url;
    info.append(title);
    const vt = item.variant_title && !item.product_has_only_default_variant ? item.variant_title : '';
    if (vt) info.append(el('p', 'line__variant', vt));

    const price = el('p', 'line__price');
    if (item.original_line_price > item.final_line_price) {
      price.append(el('s', 'pr__was', money(item.original_line_price)), ' ');
    }
    const pv = el('span', null, money(item.final_line_price));
    pv.setAttribute('data-line-price', '');
    price.append(pv);
    info.append(price);

    const ctl = el('div', 'line__ctl');
    const qty = el('div', 'qty');
    const dec = el('button', 'qty__b', '−');
    dec.type = 'button';
    dec.setAttribute('data-qty-dec', '');
    dec.setAttribute('aria-label', 'Decrease quantity of ' + item.product_title);
    if (item.quantity <= 1) dec.setAttribute('aria-disabled', 'true');
    const inp = el('input', 'qty__in');
    inp.type = 'number';
    inp.value = item.quantity;
    inp.readOnly = true;
    inp.setAttribute('aria-label', 'Quantity');
    const inc = el('button', 'qty__b', '+');
    inc.type = 'button';
    inc.setAttribute('data-qty-inc', '');
    inc.setAttribute('aria-label', 'Increase quantity of ' + item.product_title);
    qty.append(dec, inp, inc);
    const rm = el('button', 'line__rm', 'Remove');
    rm.type = 'button';
    rm.setAttribute('data-remove', '');
    rm.append(el('span', 'sr-only', ' ' + item.product_title));
    ctl.append(qty, rm);
    info.append(ctl);

    li.append(thumb, info);
    return li;
  }

  function render(cart) {
    const has = cart.item_count > 0;
    linesEl.textContent = '';
    cart.items.forEach((it) => linesEl.append(lineNode(it)));
    linesEl.hidden = !has;
    emptyEl.hidden = has;
    dialog.querySelectorAll('[data-cartonly]').forEach((n) => { n.hidden = !has; });
    subEl.textContent = money(cart.total_price);
    const remaining = threshold - cart.total_price;
    shipEl.textContent = remaining > 0 ? money(remaining) + ' away from free shipping' : "You've got free shipping";
    const count = document.getElementById('cart-count');
    if (count) {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    }
  }

  /* ---------- cart changes ---------- */
  function changedTitle(before, cart) {
    for (const it of cart.items) {
      const prev = before[it.key];
      if (!prev || it.quantity > prev.qty) return it.product_title;
    }
    return null;
  }

  async function onCartUpdated(e) {
    const d = e.detail;
    if (d && d.source === 'drawer') return;
    let cart = d && d.items ? d : null;
    if (!cart) {
      try { cart = await getJSON('/cart.js'); } catch (err) { return; }
    }
    const before = snapshot();
    render(cart);
    const t = changedTitle(before, cart);
    if (t) announce('Added ' + t);
    hidePicker();
    open();
  }
  document.addEventListener('cart:updated', onCartUpdated, { signal });

  async function change(key, quantity, focusSel) {
    if (busy) return;
    busy = true;
    linesEl.setAttribute('aria-busy', 'true');
    const before = snapshot();
    let cart;
    try {
      cart = await getJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
    } catch (err) {
      try { cart = await getJSON('/cart.js'); } catch (e2) { cart = null; }
      if (window.hssToast) window.hssToast(FAIL);
    }
    if (cart) {
      render(cart);
      const prev = before[key];
      if (quantity === 0 && prev) announce('Removed ' + prev.title);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: Object.assign({ source: 'drawer' }, cart) }));
      const target = focusSel && dialog.querySelector(focusSel);
      (target || dialog.querySelector('.line .qty__b') || closeBtn).focus();
    }
    linesEl.removeAttribute('aria-busy');
    busy = false;
  }

  linesEl.addEventListener('click', (e) => {
    const li = e.target.closest('.line');
    if (!li) return;
    const key = li.dataset.key;
    const qty = Number(li.dataset.qty);
    const sel = '.line[data-key="' + CSS.escape(key) + '"] ';
    if (e.target.closest('[data-remove]')) {
      change(key, 0, null);
    } else if (e.target.closest('[data-qty-inc]')) {
      change(key, qty + 1, sel + '[data-qty-inc]');
    } else if (e.target.closest('[data-qty-dec]')) {
      if (qty <= 1) return; // aria-disabled; Remove is the explicit way out
      change(key, qty - 1, sel + '[data-qty-dec]');
    }
  }, { signal });

  /* ---------- variant picker sheet ---------- */
  function setPickerInert(on) {
    [headEl, bodyEl, footEl].forEach((n) => { n.inert = on; });
  }
  function hidePicker() {
    pickerSeq += 1;
    picker.hidden = true;
    setPickerInert(false);
  }

  async function addVariant(variant, title) {
    const prevBusy = busy;
    if (prevBusy) return;
    busy = true;
    try {
      await getJSON('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const cart = await getJSON('/cart.js');
      render(cart);
      hidePicker();
      announce('Added ' + title);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: Object.assign({ source: 'drawer' }, cart) }));
      closeBtn.focus();
    } catch (err) {
      if (window.hssToast) window.hssToast("Couldn't add that — try again");
    } finally {
      busy = false;
    }
  }

  async function openPicker(handle) {
    const seq = ++pickerSeq;
    const from = document.activeElement !== document.body ? document.activeElement : null;
    let product;
    try {
      product = await getJSON('/products/' + encodeURIComponent(handle) + '.js');
    } catch (err) {
      if (window.hssToast) window.hssToast("Couldn't load sizes — try again");
      return;
    }
    if (seq !== pickerSeq) return;
    if (!dialog.open) open(from);
    const optName = (product.options[0] && (product.options[0].name || product.options[0])) || 'option';
    const named = String(optName).toLowerCase() === 'size' ? 'size' : 'option';
    pickerTitle.textContent = 'Select ' + named;
    pickerOpts.textContent = '';
    const multi = product.options.length > 1;
    product.variants.forEach((v) => {
      const b = el('button', 'picker__opt', multi ? v.title : v.option1 || v.title);
      b.type = 'button';
      if (!v.available) {
        b.setAttribute('aria-disabled', 'true');
        b.append(el('span', 'sr-only', ' (sold out)'));
      }
      b.addEventListener('click', () => {
        if (b.getAttribute('aria-disabled') === 'true') return;
        addVariant(v, product.title);
      }, { signal });
      pickerOpts.append(b);
    });
    picker.hidden = false;
    setPickerInert(true);
    pickerBack.focus(); // the sheet's own Back button; the x under it is inert
    announce((named === 'size' ? 'Select a size' : 'Select an option') + ' for ' + product.title);
  }
  document.addEventListener('cart:open-picker', (e) => { if (e.detail && e.detail.handle) openPicker(e.detail.handle); }, { signal });
  pickerBack.addEventListener('click', () => {
    hidePicker();
    closeBtn.focus();
  }, { signal });
  // Esc with the sheet open dismisses only the sheet.
  dialog.addEventListener('cancel', (e) => {
    if (picker.hidden) return;
    e.preventDefault();
    hidePicker();
    closeBtn.focus();
  }, { signal });
}

/* /cart page: Remove links work as plain links without JS; with JS they use the Cart API. */
function initPage(root) {
  if (root.dataset.cartInit) return;
  root.dataset.cartInit = '1';
  const ac = new AbortController();
  controllers.set(root, ac);
  root.addEventListener('click', async (e) => {
    const a = e.target.closest('[data-remove-link]');
    if (!a) return;
    e.preventDefault();
    const key = a.closest('.line').dataset.key;
    try {
      await getJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      window.location.reload();
    } catch (err) {
      window.location.href = a.href;
    }
  }, { signal: ac.signal });
}

const SEL = '[data-cart-drawer]';
const PAGE = '[data-cart-page]';
const boot = (scope) => {
  scope.querySelectorAll(SEL).forEach(init);
  scope.querySelectorAll(PAGE).forEach(initPage);
};
boot(document);
document.addEventListener('shopify:section:load', (e) => boot(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll(SEL + ',' + PAGE).forEach((r) => {
    const c = controllers.get(r);
    if (c) c.abort();
    delete r.dataset.cartInit;
  });
});
